<template>
  <div class="DataCenter">
    <SeachFilter
      @changeQuery="currentQuery"
      @resetSort="resetSort"
      :queryParams="queryParams"
      :exportLoading="exportLoading"
      @exportQuery="tableExport"
      mode="day"
    />
    <a-table
      class="tableContainer"
      :columns="dataCenterColumns"
      :transformCellText="({ text }) => text ?? '--'"
      :pagination="false"
      :data-source="TableData"
      rowKey="erp_id"
      @change="(page, filter, sorter) => sortChange(sorter)"
      :scroll="{ x: '100%', y: 'calc(100vh - 360px)' }"
      :loading="tableLoading"
    >
      <template #totalEffectiveWorkNum="{ record, text: totalEffectiveWorkNum }">
        <span class="numLink" @click="viewDetail('totalEffectiveWorkNum', record)">
          {{ totalEffectiveWorkNum }}
        </span>
      </template>
      <template #totalPointsNum="{ record, text: totalPointsNum }">
        <span class="numLink" @click="viewDetail('totalPointsNum', record)">
          {{ totalPointsNum }}
        </span>
      </template>
      <template #totalCompleteDataNum="{ record, text: totalCompleteDataNum }">
        <span class="numLink" @click="viewDetail('totalCompleteDataNum', record)">
          {{ totalCompleteDataNum }}
        </span>
      </template>
      <template #satisfactionEvaluation="{ record, text: satisfactionEvaluation }">
        <span class="numLink" @click="viewDetail('satisfactionEvaluation', record)">
          {{ satisfactionEvaluation }}
        </span>
      </template>
      <!-- ---------------------------------- -->
      <template #working_hours="{ text: working_hours }">
        <span>
          {{ handleFormatNumber(working_hours) }}
        </span>
      </template>
      <template #complete_work_order_cnt="{ record, text: complete_work_order_cnt }">
        <span class="numLink" @click="viewDetail('complete_work_order_cnt', record)">
          {{ complete_work_order_cnt }}
        </span>
      </template>
      <template #satisfied_cnt="{ record, text: satisfied_cnt }">
        <span class="numLink" @click="viewDetail('satisfied_cnt', record)">
          {{ handleFormatNumber(satisfied_cnt) }}
        </span>
      </template>
      <template #dissatisfied_cnt="{ record, text: dissatisfied_cnt }">
        <span class="numLink" @click="viewDetail('dissatisfied_cnt', record)">
          {{ handleFormatNumber(dissatisfied_cnt) }}
        </span>
      </template>
      <template #rollback_over_three_cnt="{ record, text: rollback_over_three_cnt }">
        <span class="numLink" @click="viewDetail('rollback_over_three_cnt', record)">
          {{ rollback_over_three_cnt }}
        </span>
      </template>
      <template #excellent_case_cnt="{ record, text: excellent_case_cnt }">
        <span class="numLink" @click="viewDetail('excellent_case_cnt', record)">
          {{ excellent_case_cnt }}
        </span>
      </template>
      <template #benifits="{ record, text: benifits }">
        <span class="numLink" @click="viewDetail('benifits', record)">
          {{ handleFormatNumber(benifits) }}
        </span>
      </template>
    </a-table>
    <a-row type="flex" justify="start" style="margin: 20px 0 0 20px">
      <a-col>
        <a-pagination
          v-if="Total"
          show-size-changer
          show-quick-jumper
          v-model:current="current"
          v-model:pageSize="pageSize"
          :total="Total"
          @change="listQuery"
          @showSizeChange="listQuery"
          :show-total="Total => `共${Total}条`"
        />
      </a-col>
    </a-row>
    <IndexDetail ref="Index" :query="tableQuery" />
  </div>
</template>

<script setup>
import SeachFilter from '../components/seachFilter.vue'
import { ref, reactive, nextTick, onMounted, computed } from 'vue'
import { dataAnalysePageList } from '@/api/DataCenter/index'
import { DataCenterColumns } from './config'
import useTableTools from '@/views/hooks/useTableTools'
import IndexDetail from './indexDetail.vue'
import { handleFormatNumber, formatDateForFilter } from '@/utils'
import { useFilterCache } from '@/hooks/useFilterCache'
import { FILTER_CACHE_PAGE_KEYS } from '@/constants/filterCache'
import { useStore } from 'vuex'
import { cloneDeep } from 'lodash-es'
const { normalizeExport, Total, current, pageSize, TableData } = useTableTools()
const tableLoading = ref(false)
const store = useStore()
const [defaultStartDate, defaultEndDate] = formatDateForFilter([new Date(), new Date()], true)
const defaultQuery = {
  erp_id: null,
  user_name: null,
  startDate: defaultStartDate,
  endDate: defaultEndDate,
  orderType: 'asc',
  orderField: 'complete_work_order_cnt'
}
const { queryParams, initFromCache, updateCache } = useFilterCache({
  pageKey: FILTER_CACHE_PAGE_KEYS.PERFORMANCE_STATS,
  userId: computed(() => store.state.user.userId),
  defaultParams: defaultQuery,
  normalize: params => ({
    ...cloneDeep(defaultQuery),
    ...cloneDeep(params)
  })
})
const tableQuery = computed(() => queryParams.value)
const DEFAULT_SORT = {
  orderType: 'asc',
  orderField: 'complete_work_order_cnt'
}
// 动态新增column
const orderParams = ref(null)
const dataCenterColumns = ref([])
// 排序方式
const orderTypeParams = ref(DEFAULT_SORT.orderType)
// 排序字段
const orderFieldParams = ref(DEFAULT_SORT.orderField)
const applyDefaultSort = () => {
  orderTypeParams.value = DEFAULT_SORT.orderType
  orderFieldParams.value = DEFAULT_SORT.orderField
  handleSort(orderFieldParams.value, 'ascend')
}
onMounted(() => {
  dataCenterColumns.value = cloneDeep(DataCenterColumns)
  const cacheQuery = initFromCache()
  orderTypeParams.value = cacheQuery.orderType ?? DEFAULT_SORT.orderType
  orderFieldParams.value = cacheQuery.orderField ?? DEFAULT_SORT.orderField
  handleSort(
    orderFieldParams.value,
    orderTypeParams.value === 'asc' ? 'ascend' : orderTypeParams.value === 'desc' ? 'descend' : ''
  )
  listQuery()
})
// 处理表格中升降序页面显示问题
const handleSort = (field, order) => {
  dataCenterColumns.value.forEach(column => {
    if (column['sorter']) {
      if (column['dataIndex'] === field) {
        column['sortOrder'] = order || false
      } else {
        column['sortOrder'] = false
      }
    }
  })
}
const sortChange = sorter => {
  const { field, order } = sorter
  if (order === 'ascend' || order === 'descend') {
    handleSort(field, order)
    orderTypeParams.value = order === 'ascend' ? 'asc' : 'desc'
    orderFieldParams.value = field
  } else if (field) {
    // 用户点击到取消排序时，清空排序
    handleSort('', '')
    orderTypeParams.value = ''
    orderFieldParams.value = ''
  }

  // switch (field) {
  //   case 'totalEffectiveWorkNum':
  //     orderType = 1
  //     break
  //   case 'totalPointsNum':
  //     orderType = 2
  //     break
  //   case 'totalCompleteDataNum':
  //     orderType = 3
  //     break
  //   case 'totalWorkingSeconds':
  //     orderType = 4
  //     break
  //   case 'totalCompleteWorkNum':
  //     orderType = 5
  //     break
  //   case 'satisfactionEvaluation':
  //     orderType = 6
  //     break
  //   default:
  //     break
  // }
  // orderParams.value = order ? (order === 'ascend' ? orderType : -orderType) : null
  currentQuery({
    ...queryParams.value,
    orderType: orderTypeParams.value,
    orderField: orderFieldParams.value
  })
}

const currentQuery = params => {
  tableLoading.value = true
  current.value = 1
  updateCache({
    ...params,
    orderType: orderTypeParams.value,
    orderField: orderFieldParams.value
  })
  listQuery()
}

const exportLoading = ref(false)
const tableExport = async params => {
  exportLoading.value = true
  await normalizeExport(
    '/health-doctor-assistant/doctor-assistant/data-analyse/excel',
    { ...params, orderType: orderParams.value },
    '绩效数据统计',
    'post'
  )
  exportLoading.value = false
}

const listQuery = () => {
  tableLoading.value = true
  dataAnalysePageList(
    reactive({
      offset: current.value,
      limit: pageSize.value,
      ...queryParams.value
    })
  )
    .then(res => {
      const { list, totalCount } = res
      Total.value = totalCount
      TableData.value = list
    })
    .finally(() => {
      tableLoading.value = false
    })
}

const Index = ref(null)
const viewDetail = (ctxType, ctxRecord) => {
  nextTick(() => {
    Index.value.visible = true
    Index.value.initdetail(ctxType, ctxRecord)
  })
}

// 重置表格中升降序页面显示为灰
const resetSort = () => {
  dataCenterColumns.value.forEach(column => {
    if (column.sorter) {
      column['sortOrder'] = false
    }
  })
  orderTypeParams.value = ''
  orderFieldParams.value = ''
}
</script>

<style lang="scss" scoped>
.DataCenter {
  height: 100%;
}
.DataCenter .tableContainer {
  padding: 20px 20px 0;

  .numLink {
    cursor: pointer;
    color: #1890ff;
  }
}
</style>
